import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Checkbox,
  Tooltip,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn as WaypointIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  UploadFile as UploadIcon,
  DragIndicator as DragIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Waypoint } from '../types';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedMuseum } from '../../../store/slices/museumSlice';
import acmMap from '../assets/ACM/map/map.jpg';
import ccmMap from '../assets/CCM/map/map.jpg';
import acmWaypoints from '../assets/ACM/data/waypoints.json';
import ccmWaypoints from '../assets/CCM/data/waypoints.json';
import acmPathCosts from '../assets/ACM/data/path_costs.json';
import ccmPathCosts from '../assets/CCM/data/path_costs.json';
import PathCostsTab, { PathCostsMap } from '../components/PathCostsTab';
import { PathCostVersion, waypointService, WaypointVersion } from '../services/waypointService';

const WaypointPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [showNames, setShowNames] = useState(false);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [ignoreNextClick, setIgnoreNextClick] = useState(false);
  const [pathCosts, setPathCosts] = useState<PathCostsMap>({});
  const [pathCostUploading, setPathCostUploading] = useState(false);
  const [pathCostHistoryDialogOpen, setPathCostHistoryDialogOpen] = useState(false);
  const [pathCostVersions, setPathCostVersions] = useState<PathCostVersion[]>([]);
  const [loadingPathCostVersions, setLoadingPathCostVersions] = useState(false);
  const [pathCostRestoringId, setPathCostRestoringId] = useState<number | null>(null);
  const [pendingWaypoints, setPendingWaypoints] = useState<any[] | null>(null);

  // Upload & History states
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [waypointVersions, setWaypointVersions] = useState<WaypointVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  const isCcm = selectedMuseum?.museum_code?.toUpperCase() === 'CCM';

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const nextId = useMemo(() => (waypoints.length > 0 ? Math.max(...waypoints.map(w => w.id)) + 1 : 1), [waypoints]);

  const normalizeWaypoints = useCallback(
    (items: any[]): Waypoint[] =>
      items.map((item, idx) => {
        const id = Number.isFinite(item?.id) ? Number(item.id) : idx + 1;
        const x = Number(item?.x ?? 0);
        const y = Number(item?.y ?? 0);
        return {
          id,
          title: item?.title ?? `Waypoint ${id}`,
          nav: item?.nav ?? item?.title ?? `Waypoint ${id}`,
          open_door: Boolean(item?.open_door),
          description: item?.description ?? '',
          x,
          y,
          type: item?.type ?? 'landmark',
          top: formatPercent(item?.top ?? (y && naturalSize ? (y / naturalSize.height) * 100 : 0)),
          left: formatPercent(item?.left ?? (x && naturalSize ? (x / naturalSize.width) * 100 : 0)),
        };
      }),
    [naturalSize],
  );

  const onImgLoad = () => {
    const img = imgRef.current;
    if (img) {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    }
  };

  const addWaypointFromClick = useCallback(
    (clientX: number, clientY: number) => {
      if (!imgRef.current || !containerRef.current || !naturalSize) return;

      const rect = containerRef.current.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      const displayedWidth = imgRef.current.clientWidth;
      const displayedHeight = imgRef.current.clientHeight;

      // Translate click to original image pixel coordinates
      const x = Math.round((clickX / displayedWidth) * naturalSize.width);
      const y = Math.round((clickY / displayedHeight) * naturalSize.height);

      const topPct = ((y / naturalSize.height) * 100).toFixed(2) + '%';
      const leftPct = ((x / naturalSize.width) * 100).toFixed(2) + '%';

      const newWaypoint: Waypoint = {
        id: nextId,
        title: `Waypoint ${nextId}`,
        nav: `Waypoint ${nextId}`,
        open_door: false,
        description: '',
        x,
        y,
        type: 'landmark',
        top: topPct,
        left: leftPct,
      };

      setWaypoints(prev => [...prev, newWaypoint]);
      setSelectedId(newWaypoint.id);
    },
    [naturalSize, nextId],
  );

  const formatPercent = (value: unknown) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.endsWith('%')) return trimmed;
      const num = Number(trimmed);
      if (Number.isFinite(num)) return `${num.toFixed(2)}%`;
    }
    const num = Number(value);
    if (Number.isFinite(num)) return `${num.toFixed(2)}%`;
    return '0.00%';
  };

  useEffect(() => {
    const code = selectedMuseum?.museum_code?.toUpperCase();

    if (!code) {
      setImageSrc(null);
      setNaturalSize(null);
      setWaypoints([]);
      setPathCosts({});
      setSelectedId(null);
      setImportError(null);
      setPendingWaypoints(null);
      return;
    }

    setNaturalSize(null);
    setPendingWaypoints(null);

    if (code === 'ACM') {
      setImageSrc(acmMap);
    } else if (code === 'CCM') {
      setImageSrc(ccmMap);
    } else {
      setImageSrc(null);
    }
  }, [selectedMuseum]);

  const handleJsonUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!Array.isArray(parsed)) throw new Error('File is not an array');
        const normalized: Waypoint[] = normalizeWaypoints(parsed);
        setWaypoints(normalized);
        setSelectedId(normalized[0]?.id ?? null);
        setImportError(null);
      } catch (err) {
        console.error('Failed to parse waypoints JSON', err);
        setImportError('Failed to load JSON. Ensure it is an array of waypoints with the expected fields.');
      } finally {
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setNaturalSize(null);
      setWaypoints([]);
      setSelectedId(null);
    };

    reader.onerror = err => {
      console.error('Failed to load image', err);
      setSnackbarMessage('Failed to load image');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Auto-load presets for ACM or CCM based on selected museum
  useEffect(() => {
    const code = selectedMuseum?.museum_code?.toUpperCase();
    if (!code) return;

    const loadWaypoints = async () => {
      // Try to fetch waypoints from backend first
      try {
        const backendWaypoints = await waypointService.getLatestWaypoints(code, '1');

        if (backendWaypoints && backendWaypoints.length > 0) {
          // Use backend waypoints
          setPendingWaypoints(backendWaypoints as any[]);
          setImportError(null);
          console.log('Loaded waypoints from backend');
        } else {
          // Fall back to local assets
          if (code === 'ACM') {
            setPendingWaypoints(acmWaypoints as unknown as any[]);
            setImportError(null);
            console.log('Loaded waypoints from local assets (ACM)');
          } else if (code === 'CCM') {
            setPendingWaypoints(ccmWaypoints as unknown as any[]);
            setImportError(null);
            console.log('Loaded waypoints from local assets (CCM)');
          }
        }
      } catch (error) {
        console.error('Error loading waypoints from backend, falling back to local assets:', error);
        // Fall back to local assets on error
        if (code === 'ACM') {
          setPendingWaypoints(acmWaypoints as unknown as any[]);
          setImportError(null);
        } else if (code === 'CCM') {
          setPendingWaypoints(ccmWaypoints as unknown as any[]);
          setImportError(null);
        }
      }

      // Try to fetch path costs from backend first
      try {
        const backendPathCosts = await waypointService.getLatestPathCosts(code, '1');

        if (backendPathCosts && Object.keys(backendPathCosts).length > 0) {
          // Use backend path costs
          setPathCosts(backendPathCosts.costs || backendPathCosts);
          console.log('Loaded path costs from backend');
        } else {
          // Fall back to local assets
          if (code === 'ACM') {
            setPathCosts((acmPathCosts as any)?.costs || {});
            console.log('Loaded path costs from local assets (ACM)');
          } else if (code === 'CCM') {
            setPathCosts((ccmPathCosts as any)?.costs || {});
            console.log('Loaded path costs from local assets (CCM)');
          }
        }
      } catch (error) {
        console.error('Error loading path costs from backend, falling back to local assets:', error);
        // Fall back to local assets on error
        if (code === 'ACM') {
          setPathCosts((acmPathCosts as any)?.costs || {});
        } else if (code === 'CCM') {
          setPathCosts((ccmPathCosts as any)?.costs || {});
        }
      }
    };

    loadWaypoints();
  }, [selectedMuseum]);

  // Normalize pending waypoints once we know the map's natural dimensions
  useEffect(() => {
    if (!pendingWaypoints || !naturalSize) return;

    const normalized = normalizeWaypoints(pendingWaypoints);
    setWaypoints(normalized);
    setSelectedId(normalized[0]?.id ?? null);
  }, [pendingWaypoints, naturalSize, normalizeWaypoints]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (ignoreNextClick) {
      setIgnoreNextClick(false);
      return;
    }
    if (draggingId || isDragging) return;
    // Only add waypoint if clicking empty space (not a marker)
    if ((e.target as HTMLElement).dataset['marker'] === 'true') return;
    addWaypointFromClick(e.clientX, e.clientY);
  };

  const handleMarkerClick = (id: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setSelectedId(id);
  };

  const startMarkerDrag = (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedId(id);
    setDraggingId(id);
    setIsDragging(false);
  };

  useEffect(() => {
    if (!draggingId || !naturalSize || !imgRef.current || !containerRef.current) return;

    const handleMove = (event: MouseEvent) => {
      if (!imgRef.current || !containerRef.current || !naturalSize) return;
      setIsDragging(true);

      const rect = containerRef.current.getBoundingClientRect();
      const displayedWidth = imgRef.current.clientWidth;
      const displayedHeight = imgRef.current.clientHeight;

      if (displayedWidth === 0 || displayedHeight === 0) return;

      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      const clampedX = Math.max(0, Math.min(displayedWidth, offsetX));
      const clampedY = Math.max(0, Math.min(displayedHeight, offsetY));

      const x = Math.round((clampedX / displayedWidth) * naturalSize.width);
      const y = Math.round((clampedY / displayedHeight) * naturalSize.height);

      const topPct = ((y / naturalSize.height) * 100).toFixed(2) + '%';
      const leftPct = ((x / naturalSize.width) * 100).toFixed(2) + '%';

      setWaypoints(prev => prev.map(w => (w.id === draggingId ? { ...w, x, y, top: topPct, left: leftPct } : w)));
    };

    const handleUp = () => {
      setDraggingId(null);
      setIsDragging(false);
      setIgnoreNextClick(true);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingId, naturalSize]);

  useEffect(() => {
    if (!isCcm) {
      setWaypoints(prev => (prev.some(w => w.open_door) ? prev.map(w => ({ ...w, open_door: false })) : prev));
    }
  }, [isCcm]);

  const updateWaypointField = (id: number, field: keyof Waypoint, value: any) => {
    setWaypoints(prev => prev.map(w => (w.id === id ? { ...w, [field]: value } : w)));
  };

  const removeWaypoint = (id: number) => {
    setWaypoints(prev => prev.filter(w => w.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const exportJSON = () => {
    const exported = waypoints.map(w => ({
      ...w,
      id: String(w.id),
    }));
    const dataStr = JSON.stringify(exported, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'waypoints.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadToS3 = async () => {
    if (!selectedMuseum) {
      setSnackbarMessage('Please select a museum first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (waypoints.length === 0) {
      setSnackbarMessage('No waypoints to upload');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setUploading(true);
    try {
      const waypointData = waypoints.map(w => ({
        ...w,
        id: String(w.id),
      }));

      const result = await waypointService.uploadToS3(waypointData, selectedMuseum.museum_code, '1');

      setSnackbarMessage(result.message || 'Upload success!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Failed to upload waypoints:', error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to upload waypoints');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const handleOpenHistory = async () => {
    if (!selectedMuseum) {
      setSnackbarMessage('Please select a museum first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setHistoryDialogOpen(true);
    setLoadingVersions(true);
    try {
      const versions = await waypointService.listVersions(selectedMuseum.museum_code, '1');
      setWaypointVersions(versions);
    } catch (error: any) {
      console.error('Failed to fetch waypoint versions:', error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to fetch history');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleUploadPathCosts = async () => {
    if (!selectedMuseum) {
      setSnackbarMessage('Please select a museum first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!pathCosts || Object.keys(pathCosts).length === 0) {
      setSnackbarMessage('No path costs to upload');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setPathCostUploading(true);
    try {
      const payload = { costs: pathCosts };
      const result = await waypointService.uploadPathCosts(payload, selectedMuseum.museum_code, '1');

      setSnackbarMessage(result?.message || 'Path costs uploaded successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Failed to upload path costs:', error);
      setSnackbarMessage(error?.response?.data?.error || error?.message || 'Failed to upload path costs');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setPathCostUploading(false);
    }
  };

  const handleOpenPathCostHistory = async () => {
    if (!selectedMuseum) {
      setSnackbarMessage('Please select a museum first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setPathCostHistoryDialogOpen(true);
    setLoadingPathCostVersions(true);
    try {
      const versions = await waypointService.listPathCostVersions(selectedMuseum.museum_code, '1');
      setPathCostVersions(versions);
    } catch (error: any) {
      console.error('Failed to fetch path cost versions:', error);
      setSnackbarMessage(error?.response?.data?.error || error?.message || 'Failed to fetch path cost history');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingPathCostVersions(false);
    }
  };

  const handleRestorePathCostVersion = async (versionId: number) => {
    const version = pathCostVersions.find(v => v.id === versionId);
    if (!version) return;

    setPathCostRestoringId(versionId);
    try {
      const result = await waypointService.restorePathCostVersion(versionId);

      const restoredData = result?.path_cost_version?.file_data ?? version.file_data;
      const restoredCosts = (restoredData as any)?.costs || restoredData || {};
      setPathCosts(restoredCosts);

      setSnackbarMessage(result?.message || 'Path cost version restored');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh history to reflect new active version
      if (selectedMuseum) {
        const versions = await waypointService.listPathCostVersions(selectedMuseum.museum_code, '1');
        setPathCostVersions(versions);
      }
    } catch (error: any) {
      console.error('Failed to restore path cost version:', error);
      setSnackbarMessage(error?.response?.data?.error || error?.message || 'Failed to restore path cost version');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setPathCostRestoringId(null);
      setPathCostHistoryDialogOpen(false);
    }
  };

  const handleRestoreVersion = async (versionId: number) => {
    setRestoring(true);
    try {
      const result = await waypointService.restoreVersion(versionId);

      // Reload waypoints from the restored version
      if (result.waypoint_version?.file_data) {
        const normalized = normalizeWaypoints(result.waypoint_version.file_data);
        setWaypoints(normalized);
        setSelectedId(normalized[0]?.id ?? null);
      }

      setSnackbarMessage(result.message || 'Restore success!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setHistoryDialogOpen(false);

      // Refresh history
      if (selectedMuseum) {
        const versions = await waypointService.listVersions(selectedMuseum.museum_code, '1');
        setWaypointVersions(versions);
      }
    } catch (error: any) {
      console.error('Failed to restore waypoint version:', error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to restore version');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} aria-label="waypoint tabs">
          <Tab label="Waypoints" />
          <Tab label="Path Costs" />
        </Tabs>
      </Box>

      {tabIndex === 0 && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WaypointIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Waypoints
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={showNames ? 'contained' : 'outlined'}
              color={showNames ? 'primary' : 'inherit'}
              startIcon={showNames ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => setShowNames(prev => !prev)}
            >
              Show Names
            </Button>
            <Button variant="outlined" startIcon={<UploadIcon />} component="label">
              Upload Map
              <input hidden type="file" accept="image/*" onChange={handleImageUpload} />
            </Button>
            <Button variant="outlined" startIcon={<UploadIcon />} component="label" color="secondary">
              Import JSON
              <input hidden type="file" accept="application/json" onChange={handleJsonUpload} />
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportJSON}
              disabled={waypoints.length === 0}
            >
              Export JSON
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
              onClick={handleUploadToS3}
              disabled={waypoints.length === 0 || uploading || !selectedMuseum}
            >
              {uploading ? 'Uploading...' : 'Upload to S3'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={handleOpenHistory}
              disabled={!selectedMuseum}
            >
              History
            </Button>
          </Box>
        </Box>
      )}

      {tabIndex === 0 && importError && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="error">
            {importError}
          </Typography>
        </Box>
      )}
      {tabIndex === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Click on the map to add waypoints
            </Typography>

            <Paper
              ref={containerRef}
              onClick={handleContainerClick}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                minHeight: 300,
              }}
            >
              {imageSrc ? (
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Map"
                  onLoad={onImgLoad}
                  style={{ width: '100%', height: 'auto', display: 'block', userSelect: 'none' }}
                />
              ) : (
                <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body1">Select a museum to load the default map.</Typography>
                </Box>
              )}

              {/* Markers */}
              {imageSrc &&
                waypoints.map(w => (
                  <Box
                    key={w.id}
                    sx={{
                      position: 'absolute',
                      top: w.top,
                      left: w.left,
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      pointerEvents: 'none',
                    }}
                  >
                    {showNames && (
                      <Box
                        data-marker="true"
                        sx={{
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          fontSize: 11,
                          lineHeight: 1.2,
                          pointerEvents: 'none',
                        }}
                      >
                        {w.title || `Waypoint ${w.id}`}
                      </Box>
                    )}
                    <Box
                      data-marker="true"
                      onClick={event => handleMarkerClick(w.id, event)}
                      onMouseDown={event => startMarkerDrag(w.id, event)}
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: selectedId === w.id ? 'primary.main' : 'success.main',
                        boxShadow: selectedId === w.id ? 2 : 1,
                        cursor: 'grab',
                        border: '2px solid white',
                        pointerEvents: 'auto',
                      }}
                    />
                  </Box>
                ))}
            </Paper>
          </CardContent>
        </Card>
      )}

      {tabIndex === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Waypoint Details
            </Typography>
            {waypoints.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No waypoints yet. Click on the map to add one.
              </Typography>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event: DragEndEvent) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  setWaypoints(prev => {
                    const oldIndex = prev.findIndex(w => String(w.id) === String(active.id));
                    const newIndex = prev.findIndex(w => String(w.id) === String(over.id));
                    if (oldIndex === -1 || newIndex === -1) return prev;
                    return arrayMove(prev, oldIndex, newIndex);
                  });
                }}
              >
                <SortableContext items={waypoints.map(w => w.id)} strategy={verticalListSortingStrategy}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={64}>Drag</TableCell>
                        <TableCell width={80}>ID</TableCell>
                        <TableCell>Fields</TableCell>
                        <TableCell align="center" width={90}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {waypoints.map(w => (
                        <SortableRow
                          key={w.id}
                          waypoint={w}
                          selected={selectedId === w.id}
                          onSelect={() => setSelectedId(w.id)}
                          onUpdate={updateWaypointField}
                          onRemove={removeWaypoint}
                          showOpenDoor={isCcm}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      )}

      {tabIndex === 1 && (
        <>
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WaypointIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" component="h2">
                Path Costs
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={pathCostUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                onClick={handleUploadPathCosts}
                disabled={!selectedMuseum || pathCostUploading || !pathCosts || Object.keys(pathCosts).length === 0}
              >
                {pathCostUploading ? 'Uploading...' : 'Upload to S3'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleOpenPathCostHistory}
                disabled={!selectedMuseum}
              >
                History
              </Button>
            </Box>
          </Box>

          <PathCostsTab
            waypoints={waypoints}
            imageSrc={imageSrc}
            showNames={true}
            pathCosts={pathCosts}
            setPathCosts={updater => setPathCosts(prev => updater(prev))}
          />
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Path Cost History Dialog */}
      <Dialog
        open={pathCostHistoryDialogOpen}
        onClose={() => setPathCostHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Path Cost Version History</DialogTitle>
        <DialogContent>
          {loadingPathCostVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : pathCostVersions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No path cost history available
            </Typography>
          ) : (
            <List>
              {pathCostVersions.map(version => (
                <ListItem
                  key={version.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: version.is_active ? 'action.selected' : 'background.paper',
                  }}
                >
                  <ListItemText
                    primary={`Version ${version.version} ${version.is_active ? '(Active)' : ''}`}
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          Floor: {version.floor} | Museum: {version.museum_name} ({version.museum_code})
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          Uploaded by: {version.uploaded_by || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          Uploaded: {new Date(version.uploaded_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={pathCostRestoringId === version.id ? <CircularProgress size={16} /> : <RestoreIcon />}
                    onClick={() => handleRestorePathCostVersion(version.id)}
                    disabled={pathCostRestoringId !== null || version.is_active}
                  >
                    {version.is_active ? 'Current' : 'Restore'}
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPathCostHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Waypoint Version History</DialogTitle>
        <DialogContent>
          {loadingVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : waypointVersions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No version history available
            </Typography>
          ) : (
            <List>
              {waypointVersions.map(version => (
                <ListItem
                  key={version.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: version.is_active ? 'action.selected' : 'background.paper',
                  }}
                >
                  <ListItemText
                    primary={`Version ${version.version} ${version.is_active ? '(Active)' : ''}`}
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          Floor: {version.floor} | Museum: {version.museum_name} ({version.museum_code})
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          Uploaded by: {version.uploaded_by || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          Uploaded: {new Date(version.uploaded_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={restoring ? <CircularProgress size={16} /> : <RestoreIcon />}
                    onClick={() => handleRestoreVersion(version.id)}
                    disabled={restoring || version.is_active}
                  >
                    {version.is_active ? 'Current' : 'Restore'}
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface SortableRowProps {
  waypoint: Waypoint;
  selected: boolean;
  onSelect: () => void;
  onUpdate: (id: number, field: keyof Waypoint, value: any) => void;
  onRemove: (id: number) => void;
  showOpenDoor: boolean;
}

const SortableRow: React.FC<SortableRowProps> = ({
  waypoint,
  selected,
  onSelect,
  onUpdate,
  onRemove,
  showOpenDoor,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: waypoint.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  } as React.CSSProperties;

  return (
    <TableRow ref={setNodeRef} style={style} hover selected={selected} onClick={onSelect}>
      <TableCell width={64} sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
        <DragIcon fontSize="small" />
      </TableCell>
      <TableCell width={120}>
        <TextField
          size="small"
          type="number"
          value={waypoint.id}
          onChange={e => onUpdate(waypoint.id, 'id', Number(e.target.value))}
          inputProps={{ min: 0 }}
          sx={{
            width: '100%',
            '& input[type=number]': { MozAppearance: 'textfield' },
            '& input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 0.5 }}>
          <TextField
            size="small"
            label="Title"
            value={waypoint.title}
            onChange={e => onUpdate(waypoint.id, 'title', e.target.value)}
          />
          <TextField
            size="small"
            label="Nav"
            value={waypoint.nav}
            onChange={e => onUpdate(waypoint.id, 'nav', e.target.value)}
          />
          <Select
            size="small"
            value={waypoint.type || 'landmark'}
            onChange={e => onUpdate(waypoint.id, 'type', e.target.value)}
            displayEmpty
          >
            <MenuItem value="landmark">landmark</MenuItem>
            <MenuItem value="intermediate">intermediate</MenuItem>
          </Select>

          <TextField
            size="small"
            label="Description"
            value={waypoint.description}
            onChange={e => onUpdate(waypoint.id, 'description', e.target.value)}
          />

          <TextField
            size="small"
            label="X"
            type="number"
            value={waypoint.x}
            onChange={e => onUpdate(waypoint.id, 'x', Number(e.target.value))}
          />
          <TextField
            size="small"
            label="Y"
            type="number"
            value={waypoint.y}
            onChange={e => onUpdate(waypoint.id, 'y', Number(e.target.value))}
          />
          <TextField
            size="small"
            label="Top (%)"
            value={waypoint.top}
            onChange={e => onUpdate(waypoint.id, 'top', e.target.value)}
          />
          <TextField
            size="small"
            label="Left (%)"
            value={waypoint.left}
            onChange={e => onUpdate(waypoint.id, 'left', e.target.value)}
          />

          {showOpenDoor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={waypoint.open_door}
                onChange={e => onUpdate(waypoint.id, 'open_door', e.target.checked)}
              />
              <Typography variant="body2">Open Door</Typography>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell align="center" width={90}>
        <Tooltip title="Remove waypoint">
          <IconButton color="error" onClick={() => onRemove(waypoint.id)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default WaypointPage;
